import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Check, Zap, MessageCircle, LogOut } from 'lucide-react';
import PaymentSheet from '../components/PaymentSheet';

const LOGO_URL = '/logo-stockara.png';
const WA_LINK = 'https://wa.me/6281234567890';

const PLANS = [
  {
    id: 'standard',
    name: 'Standard',
    tagline: 'Asisten',
    price: '99.000',
    credits: 2000,
    features: [
      'Manajemen Inventori',
      'Laporan Keuangan',
      '1 Channel Marketplace',
      '3 Pengguna · 500 SKU',
      'Chat Bisnis: baca data + catat transaksi',
      'Invoice & nota otomatis',
    ],
  },
  {
    id: 'plus',
    name: 'Plus',
    tagline: 'Kreator',
    price: '199.000',
    credits: 6000,
    highlight: true,
    badge: 'Populer',
    features: [
      'Semua fitur Standard +',
      'Semua Channel Marketplace',
      'Multi-Gudang · Bulk Import',
      '10 Pengguna · 2.000 SKU',
      'AI bikinkan Excel, Word, PPT',
      'Baca foto nota otomatis',
      'Ruangan tim tanpa batas',
    ],
  },
  {
    id: 'max',
    name: 'Max',
    tagline: 'Otomatis',
    price: '270.000',
    credits: 15000,
    features: [
      'Semua fitur Plus +',
      'AI CS WhatsApp',
      'Laporan otomatis ke WA',
      'AI kasih peringatan duluan',
      'Pengguna & SKU Unlimited',
      'Priority Support',
    ],
  },
];

const Paywall = () => {
  const { logout } = useAuth();
  const [payError, setPayError] = useState('');
  const [payFor, setPayFor] = useState(null);

  const handleUpgrade = (tier) => {
    setPayError('');
    setPayFor(tier);
  };

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center p-4 py-12">
      {/* Logo & header */}
      <div className="mb-8 text-center">
        <img src={LOGO_URL} alt="STOCKARA" className="h-12 mx-auto mb-3" />
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full mb-4">
          <Zap size={14} className="text-amber-400" />
          <span className="text-amber-400 text-sm font-medium">Trial 14 hari kamu sudah selesai</span>
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">Data kamu aman</h1>
        <p className="text-gray-400 text-base max-w-lg mx-auto">
          Pilih paket yang sesuai untuk terus menggunakan Stockara dan mengakses semua data bisnis kamu.
        </p>
      </div>

      {payError && (
        <div className="w-full max-w-4xl mb-4 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-xl text-sm text-red-300 text-center">
          {payError}
        </div>
      )}

      {/* Pricing cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-4xl">
        {PLANS.map((plan) => (
          <div
            key={plan.id}
            className={`relative bg-gray-900 rounded-2xl border p-6 flex flex-col transition-all ${
              plan.highlight ? 'border-emerald-500 ring-2 ring-emerald-500/40 scale-[1.03] shadow-2xl shadow-emerald-500/10' : 'border-gray-800'
            }`}
          >
            {plan.badge && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                {plan.badge}
              </div>
            )}
            <div className="mb-4">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">{plan.tagline}</p>
              <h3 className="text-lg font-bold text-white">{plan.name}</h3>
              <div className="flex items-baseline gap-1 mt-1">
                <span className="text-xs text-gray-400">Rp</span>
                <span className="text-3xl font-bold text-white">{plan.price}</span>
                <span className="text-xs text-gray-400">/bln</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 mb-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-2.5 py-1.5 w-fit">
              <Zap size={12} className="text-emerald-400" />
              <span className="text-xs font-semibold text-emerald-300">{plan.credits.toLocaleString('id-ID')} Kredit AI/bulan</span>
            </div>
            <ul className="space-y-2 flex-1 mb-6">
              {plan.features.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check size={14} className="mt-0.5 flex-shrink-0 text-emerald-400" />
                  <span className="text-sm text-gray-300">{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => handleUpgrade(plan.id)}
              className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-all flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700"
            >
              {`Pilih ${plan.name}`}
            </button>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="mt-8 text-center space-y-3">
        <p className="text-xs text-gray-600">
          Pembayaran aman langsung di halaman ini, QRIS, Virtual Account, e-wallet.
        </p>
        <a
          href={WA_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
        >
          <MessageCircle size={16} />
          Butuh bantuan? Chat kami di WhatsApp
        </a>
        <div>
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 text-xs text-gray-600 hover:text-gray-400 transition-colors"
          >
            <LogOut size={14} />
            Keluar dari akun
          </button>
        </div>
      </div>

      {payFor && (
        <PaymentSheet
          kind="subscription"
          tier={payFor}
          title={`Langganan Stockara ${payFor.charAt(0).toUpperCase() + payFor.slice(1)}`}
          subtitle="Pilih cara bayar yang paling gampang buat kamu"
          onClose={() => setPayFor(null)}
          onPaid={() => { setTimeout(() => window.location.reload(), 1200); }}
        />
      )}
    </div>
  );
};

export default Paywall;
